import { LessonGalleryItem } from "@/components/common/lesson-gallery/lesson-gallery";
import { IndependentLocales, TeacherClassGallery, TeamLocales } from "@/locales/localeid";
import { computed, defineComponent, ref } from "vue";
import { fmtMsg } from "vue-glcommonui";
import { useStore } from "vuex";
import { Tooltip } from "ant-design-vue";
import { PlusIcon, VideoCameraIcon, VideoCameraSlashIcon } from "@heroicons/vue/20/solid";
import { useRemoteRequests } from "@/hooks/use-remote-requests.";
import { MediaDeviceTypes } from "@/utils/utils";
import { RoomStore, useRoomStore } from "@/hooks/use-room-store";
import { debounce } from "lodash";
import { StudentState } from "@/store/room/interface";
import { LoadingOutlined } from "@ant-design/icons-vue";
import { vuexName, MODULES } from "@/store/utils";

export default defineComponent({
  name: "StudentControls",
  props: ["isTeamMode"],
  components: {
    Tooltip,
    VideoCameraIcon,
    VideoCameraSlashIcon,
    PlusIcon,
    LoadingOutlined,
  },
  setup(props) {
    const { getters, dispatch } = useStore();
    const { onlineStudentIdsNotInOneToOneMode, isAllOnlineStudentsVideoHidden, isAllOnlineStudentsAudioMuted, isAllOnlineStudentsDisabledPalette } =
      useRoomStore(RoomStore.Teacher);
    const { requestToggleStudentMediaDevices, requestToggleStudentPalette, requestSetStudentsBadge } = useRemoteRequests();
    const isLoadingCamera = ref(false);
    const isLoadingMicrophone = ref(false);
    const isLoadingPalette = ref(false);
    const isLoadingBadge = ref(false);
    const loadingIndependent = ref(false);
    const stickerAllText = computed(() => fmtMsg(TeacherClassGallery.StickerAll));
    const unmuteAllText = computed(() => fmtMsg(TeacherClassGallery.UnmuteAll));
    const muteAllText = computed(() => fmtMsg(TeacherClassGallery.MuteAll));
    const enableAllText = computed(() => fmtMsg(TeacherClassGallery.EnableAll));
    const disableAllText = computed(() => fmtMsg(TeacherClassGallery.DisableAll));
    const showAllText = computed(() => fmtMsg(TeacherClassGallery.ShowAll));
    const hideAllText = computed(() => fmtMsg(TeacherClassGallery.HideAll));
    const students = computed<Array<StudentState>>(() => getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENTS)]);
    const callingUserIds = computed<Array<string>>(() => getters[vuexName(MODULES.CALLING.GETTERS.GET_CALLING_USER_IDS)]);

    const toggleDevice = async (deviceType: MediaDeviceTypes, isAllDeviceMuted: boolean) => {
      if (deviceType === MediaDeviceTypes.Camera) {
        isLoadingCamera.value = true;
      } else {
        isLoadingMicrophone.value = true;
      }
      const isMute = !isAllDeviceMuted;
      const studentIds = onlineStudentIdsNotInOneToOneMode.value;
      const payload = { mediaDeviceType: deviceType, isMute, studentIds };
      await requestToggleStudentMediaDevices(payload);
      if (deviceType === MediaDeviceTypes.Camera) {
        isLoadingCamera.value = false;
      } else {
        isLoadingMicrophone.value = false;
      }
    };

    const toggleVideo = debounce(() => toggleDevice(MediaDeviceTypes.Camera, isAllOnlineStudentsVideoHidden.value), 150);
    const toggleAudio = debounce(() => toggleDevice(MediaDeviceTypes.Microphone, isAllOnlineStudentsAudioMuted.value), 150);

    const onClickBadgeAll = async () => {
      isLoadingBadge.value = true;
      const onlineStudents = students.value.filter((s) => callingUserIds.value.includes(s.id)).map((s) => s.id);
      await requestSetStudentsBadge(onlineStudents);
      isLoadingBadge.value = false;
    };
    const onClickDisableAllPalette = async () => {
      isLoadingPalette.value = true;
      const currentPaletteStudent: StudentState | undefined = students.value.find((student) => student.isPalette);
      if (!currentPaletteStudent) return;

      await requestToggleStudentPalette({
        studentId: currentPaletteStudent.id,
        isEnable: false,
        isHelper: getters[vuexName(MODULES.TEACHER.GETTERS.GET_IS_HELPER)],
        // This button is only pressed during a normal session, so isOneOne is always false
        isOneOne: false,
      });
      isLoadingPalette.value = false;
    };
    const toggleIndependentMode = async () => {
      loadingIndependent.value = true;
      await dispatch(vuexName(MODULES.TEACHER_TEACHING.DISPATCHES.TOGGLE_INDEPENDENT_MODE));
      loadingIndependent.value = false;
    };
    const isIndependentModeEnabled = computed(() => getters[vuexName(MODULES.CLASS_TEACHING.GETTERS.GET_IS_INDEPENDENT_MODE_ENABLED)]);
    const independentModeContent = computed<LessonGalleryItem[] | null>(() => {
      return getters[vuexName(MODULES.LESSON.GETTERS.GET_LIST_READER_ITEMS_OF_CURRENT_EXPOSURE)];
    });
    const independentModeShown = computed<boolean>(() => {
      const isHelper = getters[vuexName(MODULES.TEACHER.GETTERS.GET_IS_HELPER)];
      if (isHelper) return false;
      return independentModeContent.value ? independentModeContent.value.length > 0 : false;
    });
    const isDisableToggleIndependentModeBtn = computed(() => (props.isTeamMode && !isIndependentModeEnabled.value) || loadingIndependent.value);
    const independentButtonText = computed(() =>
      props.isTeamMode && !isIndependentModeEnabled.value
        ? MsgPreventToggleIMWarning.value
        : isIndependentModeEnabled.value
        ? MsgStopIndependentMode.value
        : MsgStartIndependentMode.value,
    );

    //** Messages */
    const MsgTeamModeName = computed(() => fmtMsg(TeamLocales.TeamMode));
    const MsgPreventToggleIMWarning = computed(() =>
      fmtMsg(IndependentLocales.PreventToggleIndependentModeWarning, { modeName: MsgTeamModeName.value }),
    );
    const MsgStopIndependentMode = computed(() => fmtMsg(IndependentLocales.StopMode));
    const MsgStartIndependentMode = computed(() => fmtMsg(IndependentLocales.StartMode));
    return {
      onClickDisableAllPalette,
      onClickBadgeAll,
      toggleVideo,
      toggleAudio,
      isAllOnlineStudentsVideoHidden,
      isAllOnlineStudentsAudioMuted,
      isAllOnlineStudentsDisabledPalette,
      stickerAllText,
      unmuteAllText,
      muteAllText,
      enableAllText,
      disableAllText,
      showAllText,
      hideAllText,
      toggleIndependentMode,
      isIndependentModeEnabled,
      loadingIndependent,
      MsgStopIndependentMode,
      MsgStartIndependentMode,
      VideoCameraIcon,
      VideoCameraSlashIcon,
      independentModeShown,
      isDisableToggleIndependentModeBtn,
      independentButtonText,
      isLoadingBadge,
      isLoadingCamera,
      isLoadingMicrophone,
      isLoadingPalette,
    };
  },
});
